import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import { resolveCart, CartData, CartItemData } from "@/services/cartApi";
import {
  addCartItem,
  removeCartItem,
  updateCartItemQuantity,
  clearCartItems,
} from "@/services/cartItemsApi";
import { withRetry } from "@/utils/retry";
import { useSessionContext } from "@/contexts/SessionContext";
import { useBrand } from "@/contexts/BrandContext";
import { getSession } from "@/services/sessionStorage";

/* ------------------------------------------------------------------ */
/*  Empty cart data                                                     */
/* ------------------------------------------------------------------ */

const EMPTY_CART_DATA: CartData = {
  cart_id: "",
  is_new: true,
  items: [],
  subtotal: 0,
  discount: 0,
  delivery_fee: 0,
  is_delivery_free: false,
  tax: 0,
  total: 0,
  items_count: 0,
  promo_code: null,
  restaurant_id: null,
  discount_enabled: true,
  created_at: "",
  expires_at: "",
};

/* ------------------------------------------------------------------ */
/*  CartItem type for add-to-cart input                                */
/* ------------------------------------------------------------------ */

export type CartItem = {
  id: string;
  menuItem: {
    id: string;
    name: string;
    nameAr: string;
    image: string;
    price: number;
    [key: string]: unknown;
  };
  quantity: number;
  size?: string;
  extras: string[];
  removed: string[];
  specialInstructions: string;
  unitPrice: number;
  selectedFeatures?: CartItemData["customizations"]["selected_features"];
  featureSummary?: CartItemData["customizations"]["feature_summary"];
};

/* ------------------------------------------------------------------ */
/*  Context type                                                       */
/* ------------------------------------------------------------------ */

interface CartContextType {
  cartId: string | null;
  cartData: CartData;
  isLoading: boolean;
  isMutating: boolean;
  isResolved: boolean;
  error: { code: string; message: string } | null;
  isEmpty: boolean;
  itemsCount: number;
  total: number;

  initializeCart: () => Promise<void>;
  refreshCart: (overrides?: { orderType?: string, lat?: number, lon?: number, branchId?: string }) => Promise<void>;
  resetCart: () => Promise<void>;

  addItem: (item: CartItem) => Promise<void>;
  removeItem: (cartItemId: string) => Promise<void>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  /** Immediately clears cartId from all locations (ref, state, session storage) - use after place-order */
  clearCartIdEverywhere: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function CartProvider({ children }: { children: ReactNode }) {
  const { session, setCartId: setSessionCartId, clearCartId: clearSessionCartId, setCartItemsLength, setBranchStatus } = useSessionContext();
  const { brand } = useBrand();

  const [cartData, setCartData] = useState<CartData>(EMPTY_CART_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [isResolved, setIsResolved] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  const prevOrderTypeRef = useRef(session.order.order_type);
  const prevBranchIdRef = useRef(session.order.branchId);
  const resolveRef = useRef(0);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cartIdRef = useRef(session.order.cartId || "");

  /* ---------- Helpers ---------- */

  const applyCartData = useCallback(
    (data: CartData, newCartId?: string) => {
      const resolvedCartId = newCartId ?? data.cart_id ?? "";
      cartIdRef.current = resolvedCartId;

      setCartData(data);
      if (newCartId) setSessionCartId(newCartId);
      setCartItemsLength(data.items_count);
      if (data.branch_status) {
        setBranchStatus(data.branch_status.is_open, data.branch_status.is_busy, data.branch_status.reason ?? null);
      }
    },
    [setSessionCartId, setCartItemsLength, setBranchStatus]
  );

  const scheduleRefresh = useCallback((expiresAt: string) => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    if (!expiresAt) return;
    const refreshIn = new Date(expiresAt).getTime() - Date.now() - 60_000;
    if (refreshIn > 0) {
      expiryTimerRef.current = setTimeout(() => {
        refreshCart();
      }, refreshIn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Resolution ---------- */

  const initializeCart = useCallback(async () => {
    const thisResolve = ++resolveRef.current;
    setIsLoading(true);
    setError(null);

    const currentCartId = cartIdRef.current || session.order.cartId;
    const branchId = session.order.branchId??null;
    const orderType = session.order.order_type??null;
    const lat= session.customer.lastCoordinates?.latitude ?? null;
    const lon= session.customer.lastCoordinates?.longitude ?? null;
    const phone_number = session.customer.data?.mobile?.toString() ?? null;
    let result = await withRetry(() => resolveCart(currentCartId || null, brand.id || null, branchId, orderType, lat, lon, phone_number));
    if (thisResolve !== resolveRef.current) return;

    if (!result.success && result.error?.code === "CART_INVALID") {
      // Only clear the ref — do NOT call setSessionCartId("") here,
      // because that triggers the branch/orderType effect which would
      // call resetCart() concurrently, causing a race condition that
      // can leave isLoading stuck and produce a blank screen.
      cartIdRef.current = "";
      result = await withRetry(() => resolveCart(null, brand.id || null));
      if (thisResolve !== resolveRef.current) return;
    }

    if (result.success && result.cartData) {
      applyCartData(result.cartData, result.cartId!);
      scheduleRefresh(result.cartData.expires_at);
    } else {
      setError(result.error || { code: "UNKNOWN", message: "Failed to resolve cart" });
    }

    setIsLoading(false);
    setIsResolved(true);
  }, [session.order.cartId, setSessionCartId, applyCartData, scheduleRefresh, brand.id]);

  const refreshCart = useCallback(async (overrides?: { orderType?: string,  lat?: number | null;lon?: number | null; branchId?: string | null }) => {
    const thisResolve = ++resolveRef.current;
    const currentCartId = cartIdRef.current || session.order.cartId;
  

      // ✅ Read fresh from localStorage — avoids stale closure on first render
    const freshSession = getSession();
    const branchId = overrides?.branchId !== undefined ? overrides.branchId : (freshSession.order.branchId ?? null); 
    const orderType = overrides?.orderType ?? freshSession.order.order_type??null;
    const lat = overrides?.lat !== undefined ? overrides.lat : (freshSession.customer.lastCoordinates?.latitude ?? null);
    const lon = overrides?.lon !== undefined ? overrides.lon : (freshSession.customer.lastCoordinates?.longitude ?? null);
    const phone_number = freshSession.customer.data?.mobile?.toString() ?? null;

    const result = await withRetry(() => resolveCart(currentCartId || null, brand.id || null, branchId, orderType, lat, lon, phone_number));
    if (thisResolve !== resolveRef.current) return;

    if (result.success && result.cartData) {
      applyCartData(result.cartData, result.cartId || undefined);
      scheduleRefresh(result.cartData.expires_at);
    } else if (result.error?.code === "CART_INVALID") {
      cartIdRef.current = "";
      setSessionCartId("");
      const newResult = await withRetry(() => resolveCart(null, brand.id || null));
      if (thisResolve !== resolveRef.current) return;
      if (newResult.success && newResult.cartData) {
        applyCartData(newResult.cartData, newResult.cartId!);
        scheduleRefresh(newResult.cartData.expires_at);
      }
    }
  }, [session.order.cartId, setSessionCartId, applyCartData, scheduleRefresh, brand.id]);

  const resetCart = useCallback(async () => {
    const thisResolve = ++resolveRef.current;
    setIsLoading(true);
    cartIdRef.current = "";
    setSessionCartId("");
 
    const result = await withRetry(() => resolveCart(null, brand.id || null));
    if (thisResolve !== resolveRef.current) return;

    if (result.success && result.cartData) {
      applyCartData(result.cartData, result.cartId!);
      scheduleRefresh(result.cartData.expires_at);
      setError(null);
    } else {
      setError(result.error || { code: "UNKNOWN", message: "Failed to create new cart" });
    }
    setIsLoading(false);
  }, [setSessionCartId, applyCartData, scheduleRefresh, brand.id]);

  /* ---------- Cart item mutations (backend) ---------- */

  const ensureCartId = useCallback(async (): Promise<string | null> => {
    if (cartIdRef.current) return cartIdRef.current;
    await initializeCart();
    return cartIdRef.current || null;
  }, [initializeCart]);

  const addItem = useCallback(
    async (item: CartItem) => {
      setIsMutating(true);
      const cid = await ensureCartId();
      if (!cid) {
        setIsMutating(false);
        return;
      }

      // Check if an identical item already exists in the cart
      const existing = cartData.items.find((ci) => {
        if (ci.item_id !== item.menuItem.id) return false;
        const c = ci.customizations;
        const sameFeatures = JSON.stringify(c.selected_features || []) === JSON.stringify(item.selectedFeatures || []);
        const sameRemoved =
          c.removed_ingredients.length === item.removed.length &&
          c.removed_ingredients.every((r) => item.removed.includes(r));
        const sameInstructions = (c.special_instructions ?? "") === (item.specialInstructions ?? "");
        return sameFeatures && sameRemoved && sameInstructions;
      });

      const orderType = session.order.order_type;
      const rid = brand.id;
      const branchId = session.order.branchId;
      let result;
      if (existing) {
        result = await updateCartItemQuantity(cid, existing.id, existing.quantity + item.quantity, orderType, rid, branchId, session.customer.lastCoordinates ? {
          latitude: session.customer.lastCoordinates.latitude??0,
          longitude: session.customer.lastCoordinates.longitude??0,
        } : null, session.order.distance_km ?? 0, session.customer.data?.mobile?.toString() ?? null);
      } else {
        result = await addCartItem(cid, {
          item_id: item.menuItem.id,
          name: item.menuItem.name,
          name_ar: item.menuItem.nameAr,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          size: item.size || null,
          extras: item.extras,
          removed_ingredients: item.removed,
          selected_features: item.selectedFeatures || [],
          special_instructions: item.specialInstructions,
          geolocation_coordinates: session.customer.lastCoordinates ? {
              latitude: session.customer.lastCoordinates.latitude??0,
              longitude: session.customer.lastCoordinates.longitude??0,
            }
          : null,
          distance_km: session.order.distance_km ?? 0,
          phone_number: session.customer.data?.mobile?.toString() ?? null,
        }, orderType, rid, branchId);
      }

      if (result.branchStatus) {
        setBranchStatus(result.branchStatus.is_open, result.branchStatus.is_busy, result.branchStatus.reason ?? null);
      }

      if (result.success && result.cartData) {
        applyCartData(result.cartData);
      } else {
        setError(result.error);
      }
      setIsMutating(false);
    },
    [ensureCartId, applyCartData, cartData.items, session.order.order_type, session.order.branchId, brand.id, setBranchStatus]
  );

  const removeItem = useCallback(
    async (cartItemId: string) => {
      const cid = cartIdRef.current || session.order.cartId || cartData.cart_id;
      console.log("[CartContext] removeItem called", { cartItemId, cid, refVal: cartIdRef.current, sessionCartId: session.order.cartId, dataCartId: cartData.cart_id });
      if (!cid) {
        console.warn("[CartContext] removeItem: no cart id available");
        return;
      }
      if (!cartItemId) {
        console.warn("[CartContext] removeItem: no cartItemId provided");
        return;
      }
      setIsMutating(true);

      const freshSession = getSession();

      const result = await removeCartItem(cid, cartItemId, freshSession.order.order_type, brand.id, freshSession.order.branchId, freshSession.customer.lastCoordinates ? {
        latitude: freshSession.customer.lastCoordinates.latitude??0,
        longitude: freshSession.customer.lastCoordinates.longitude??0,
      } : null, freshSession.order.distance_km ?? 0, freshSession.customer.data?.mobile?.toString() ?? null);
      console.log("[CartContext] removeItem result", result);
      if (result.branchStatus) {
        setBranchStatus(result.branchStatus.is_open, result.branchStatus.is_busy, result.branchStatus.reason ?? null);
      }
      if (result.success && result.cartData) {
        applyCartData(result.cartData);
      } else if (result.error?.code === "CART_INVALID") {
        await initializeCart();
      } else {
        setError(result.error);
      }
      setIsMutating(false);
    },
    [session.order.cartId, cartData.cart_id, applyCartData, initializeCart, session.order.order_type, session.order.branchId, brand.id, setBranchStatus]
  );

  const updateQuantity = useCallback(
    async (cartItemId: string, quantity: number) => {
      const cid = cartIdRef.current || session.order.cartId || cartData.cart_id;
      console.log("[CartContext] updateQuantity called", { cartItemId, quantity, cid, refVal: cartIdRef.current, sessionCartId: session.order.cartId, dataCartId: cartData.cart_id });
      if (!cid) {
        console.warn("[CartContext] updateQuantity: no cart id available");
        return;
      }
      if (!cartItemId) {
        console.warn("[CartContext] updateQuantity: no cartItemId provided");
        return;
      }
      setIsMutating(true);
      const freshSession = getSession();
      const result = await updateCartItemQuantity(cid, cartItemId, quantity, freshSession.order.order_type, brand.id, freshSession.order.branchId, freshSession.customer.lastCoordinates ? {
        latitude: freshSession.customer.lastCoordinates.latitude??0,
        longitude: freshSession.customer.lastCoordinates.longitude??0,
      } : null, freshSession.order.distance_km ?? 0, freshSession.customer.data?.mobile?.toString() ?? null);
      console.log("[CartContext] updateQuantity result", result);
      if (result.branchStatus) {
        setBranchStatus(result.branchStatus.is_open, result.branchStatus.is_busy, result.branchStatus.reason ?? null);
      }
      if (result.success && result.cartData) {
        applyCartData(result.cartData);
      } else if (result.error?.code === "CART_INVALID") {
        await initializeCart();
      } else {
        setError(result.error);
      }
      setIsMutating(false);
    },
    [session.order.cartId, cartData.cart_id, applyCartData, initializeCart, session.order.order_type, session.order.branchId, brand.id, setBranchStatus]
  );

  const clearCart = useCallback(async () => {
    const cid = cartIdRef.current || session.order.cartId || cartData.cart_id;
    if (!cid) return;
    setIsMutating(true);

    const result = await clearCartItems(cid, session.order.order_type, brand.id, session.order.branchId);
    if (result.branchStatus) {
      setBranchStatus(result.branchStatus.is_open, result.branchStatus.is_busy, result.branchStatus.reason ?? null);
    }
    if (result.success) {
      if (result.cartData) {
        applyCartData(result.cartData);
      } else {
        // Cart was already consumed (e.g. after place-order) — reset to fresh cart
        cartIdRef.current = "";
        setSessionCartId("");
        setCartData(EMPTY_CART_DATA);
        setCartItemsLength(0);
      }
      setError(null);
    } else {
      setError(result.error);
    }
    setIsMutating(false);
  }, [session.order.cartId, cartData.cart_id, applyCartData, setSessionCartId, setCartItemsLength, session.order.order_type, session.order.branchId, brand.id, setBranchStatus]);

  /**
   * Immediately clears cartId from ALL locations:
   * - cartIdRef (in-memory ref)
   * - cartData state (cart_id field)
   * - session storage (via clearSessionCartId which writes immediately)
   * Use this after place-order to ensure no stale cartId can be restored.
   */
  const clearCartIdEverywhere = useCallback(() => {
    // 1. Clear the ref
    cartIdRef.current = "";
    // 2. Clear the cartData state (set cart_id to empty)
    setCartData(prev => ({ ...prev, cart_id: "" }));
    // 3. Clear session storage immediately (this writes to localStorage synchronously)
    clearSessionCartId();
    // 4. Reset cart items length
    setCartItemsLength(0);
  }, [clearSessionCartId, setCartItemsLength]);

  /* ---------- Effects ---------- */

  useEffect(() => {
    cartIdRef.current = session.order.cartId || cartData.cart_id || "";
  }, [session.order.cartId, cartData.cart_id]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        if (cartData.expires_at && new Date(cartData.expires_at).getTime() < Date.now()) {
          refreshCart();
        } else if (!isResolved) {
          initializeCart();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [cartData.expires_at, isResolved, refreshCart, initializeCart]);

  useEffect(() => {
    return () => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    initializeCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentOrderType = session.order.order_type;
    const currentBranchId = session.order.branchId;
    const orderTypeChanged = prevOrderTypeRef.current !== currentOrderType;
    const branchChanged = prevBranchIdRef.current !== currentBranchId;

    // Skip on first render (hydration) — refs start with initial session values
    if (!isResolved) {
      prevOrderTypeRef.current = currentOrderType;
      prevBranchIdRef.current = currentBranchId;
      return;
    }

    prevOrderTypeRef.current = currentOrderType;
    prevBranchIdRef.current = currentBranchId;

    if (!orderTypeChanged && !branchChanged) return;

    // Deterministic reset: only reset if cartId was intentionally cleared
    // by setBranch/setOrderType with preserveCart=false (which sets cartId to "").
    // When preserveCart=true (shared products), cartId remains set → no reset.
    const cartIdCleared = !session.order.cartId;

    if (import.meta.env.DEV) {
      console.log("[CartContext] branch/orderType change detected", {
        orderTypeChanged, branchChanged, cartIdCleared,
        currentCartId: session.order.cartId,
        refCartId: cartIdRef.current,
      });
    }

    if (cartIdCleared) {
      resetCart();
    }
  }, [session.order.order_type, session.order.branchId, session.order.cartId, resetCart, isResolved]);

  /* ---------- Computed ---------- */

  const cartId = session.order.cartId || null;
  const isEmpty = cartData.items_count === 0;
  const itemsCount = cartData.items_count;
  const total = cartData.total;

  const value = useMemo<CartContextType>(
    () => ({
      cartId,
      cartData,
      isLoading,
      isMutating,
      isResolved,
      error,
      isEmpty,
      itemsCount,
      total,
      initializeCart,
      refreshCart,
      resetCart,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      clearCartIdEverywhere,
    }),
    [
      cartId, cartData, isLoading, isMutating, isResolved, error, isEmpty, itemsCount, total,
      initializeCart, refreshCart, resetCart,
      addItem, removeItem, updateQuantity, clearCart, clearCartIdEverywhere,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
}
